import type {
  DocumentReferencePublic,
  PartInstancePublic,
  QuoteDetailPublic,
  WorkflowStepPublic,
  WorkPackageOutcome,
  WorkPackageTaskPublic,
  WorkPackageWithTasksPublic,
  Workscope,
} from "@common/client"

export type Finding = {
  step: WorkflowStepPublic
  task: WorkPackageTaskPublic
}

export function travellerOf(
  quote: QuoteDetailPublic,
): WorkPackageWithTasksPublic | null {
  return quote.kind === "traveller" ? quote.work_package : null
}

export function unitText(unit: PartInstancePublic): string {
  return [unit.part.number, unit.serial_number && `S/N ${unit.serial_number}`]
    .filter(Boolean)
    .join(" · ")
}

export function kindLabel(kind: Workscope): string {
  switch (kind) {
    case "test":
      return "Test"
    case "repair":
      return "Repair"
    case "overhaul":
      return "Overhaul"
    case "warranty":
      return "Warranty"
  }
}

function outcomeLabel(outcome: WorkPackageOutcome): string {
  switch (outcome) {
    case "certified":
      return "Certified"
    case "repaired":
      return "Repaired"
    case "overhauled":
      return "Overhauled"
    case "returned_as_is":
      return "Returned as is"
    case "scrapped":
      return "Scrapped"
  }
}

export function classificationText(
  pkg: WorkPackageWithTasksPublic,
): string | null {
  const parts = [
    pkg.workscope ? kindLabel(pkg.workscope) : null,
    pkg.outcome ? outcomeLabel(pkg.outcome) : null,
  ].filter((part) => part !== null)
  return parts.length > 0 ? parts.join(" → ") : null
}

export function findingsOf(pkg: WorkPackageWithTasksPublic): Finding[] {
  const tasks = new Map(
    (pkg.tasks ?? []).flatMap((task) =>
      task.workflow_step_id ? [[task.workflow_step_id, task] as const] : [],
    ),
  )
  return (pkg.workflow?.active_steps ?? [])
    .filter((step) => step.is_active)
    .flatMap((step) => {
      const task = tasks.get(step.id)
      return task ? [{ step, task }] : []
    })
}

export function stepReferences(
  pkg: WorkPackageWithTasksPublic,
): DocumentReferencePublic[] {
  return (pkg.tasks ?? []).flatMap((task) => task.document_references ?? [])
}
